import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { InseratStatus } from "@/api/inserate";
import {
  EMPTY_FILTERS,
  type InserateFilters as Filters,
} from "./filterModel";

interface Props {
  value: Filters;
  onChange: (next: Filters) => void;
}

const STATUS_OPTIONS: { value: InseratStatus | "all"; label: string }[] = [
  { value: "all", label: "Alle Status" },
  { value: "neu", label: "Neu" },
  { value: "shortlist", label: "Shortlist" },
  { value: "in-analyse", label: "In Analyse" },
  { value: "gekauft", label: "Gekauft" },
  { value: "dismissed", label: "Verworfen" },
  { value: "expired", label: "Abgelaufen" },
];

/** Converts a number-input string to `number | null`. Empty / invalid → null. */
function parseNumber(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function InserateFilters({ value, onChange }: Props) {
  const set = <K extends keyof Filters>(key: K, v: Filters[K]) =>
    onChange({ ...value, [key]: v });

  const isPristine =
    value.status === EMPTY_FILTERS.status &&
    value.quelle === EMPTY_FILTERS.quelle &&
    value.stadt === EMPTY_FILTERS.stadt &&
    value.preisMin == null &&
    value.preisMax == null &&
    value.preisProM2Min == null &&
    value.preisProM2Max == null;

  return (
    <section
      className="rounded-lg border p-4 space-y-3"
      data-testid="inserate-filters"
      aria-label="Filter"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-muted-foreground">Filter</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isPristine}
          onClick={() => onChange(EMPTY_FILTERS)}
          data-testid="filter-reset"
        >
          Zurücksetzen
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs font-medium">Status</span>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={value.status}
            onChange={(e) =>
              set("status", e.target.value as InseratStatus | "all")
            }
            data-testid="filter-status"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium">Quelle</span>
          <Input
            value={value.quelle}
            onChange={(e) => set("quelle", e.target.value)}
            placeholder="z.B. immoscout24"
            data-testid="filter-quelle"
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-medium">Stadt</span>
          <Input
            value={value.stadt}
            onChange={(e) => set("stadt", e.target.value)}
            placeholder="z.B. Hannover"
            data-testid="filter-stadt"
          />
        </label>

        <fieldset className="space-y-1">
          <legend className="text-xs font-medium">Preis (€)</legend>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={value.preisMin ?? ""}
              onChange={(e) => set("preisMin", parseNumber(e.target.value))}
              placeholder="min"
              data-testid="filter-preis-min"
            />
            <span aria-hidden className="text-muted-foreground">–</span>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={value.preisMax ?? ""}
              onChange={(e) => set("preisMax", parseNumber(e.target.value))}
              placeholder="max"
              data-testid="filter-preis-max"
            />
          </div>
        </fieldset>

        <fieldset className="space-y-1 sm:col-span-2 lg:col-span-2">
          <legend className="text-xs font-medium">€/m²</legend>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={value.preisProM2Min ?? ""}
              onChange={(e) =>
                set("preisProM2Min", parseNumber(e.target.value))
              }
              placeholder="min"
              data-testid="filter-ppm-min"
            />
            <span aria-hidden className="text-muted-foreground">–</span>
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              value={value.preisProM2Max ?? ""}
              onChange={(e) =>
                set("preisProM2Max", parseNumber(e.target.value))
              }
              placeholder="max"
              data-testid="filter-ppm-max"
            />
          </div>
        </fieldset>
      </div>
    </section>
  );
}
